import { NgModule, CUSTOM_ELEMENTS_SCHEMA, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { createCustomElement } from '@angular/elements';
import { CookieService } from 'ngx-cookie-service';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgReduxModule } from '@ptl/angular-redux-store';
import { NgReduxFormModule } from '@ptl/angular-redux-form';

import { MaterialGlobalModule } from 'src/app/shared/modules/material-global.module';
import {
  ModuleRegistry,
  InfiniteRowModelModule,
  TextFilterModule,
  ValidationModule
} from 'ag-grid-community';
import {
  ServerSideRowModelModule,
  MasterDetailModule,
  RichSelectModule,
  ColumnsToolPanelModule,
  LicenseManager
} from 'ag-grid-enterprise';

import { EnrollButtonComponent } from './components/enroll-button/enroll-button.component';
import { ReducersRegistryModule } from 'src/app/shared/modules/reducers-registry.module';
import { CurrentCountyAndZipChangeComponent } from './components/current-county-and-zip-change/current-county-and-zip-change.component';
import { LoginPageComponent } from './components/login-page/login-page.component';
import { NeedMoreHelpComponent } from './components/need-more-help/need-more-help.component';
import { NeedMoreHelpModalComponent } from './components/need-more-help/need-more-help-modal/need-more-help-modal.component';

import { PasswordResetComponent } from './components/password-reset/password-reset.component';
import { PrintButtonComponent } from './components/print-button/print-button.component';
import { RegistrationComponent } from './components/registration/registration.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { AppStateAsContentComponent } from './components/app-state-as-content/app-state-as-content.component';
import { LogoutComponent } from './logout/logout.component';
import { EmailPageComponent } from './components/email-page/email-page.component';
import { GlobalHiddenComponent } from './components/global-hidden/global-hidden.component';
import { MemberLookupComponent } from './components/member-lookup/member-lookup.component';
import { LeavingGateModalComponent } from './components/global-hidden/leaving-gate-modal/leaving-gate-modal.component';
import { RrdLinkComponent } from './components/rrd-link/rrd-link.component';
import { WhbBannerComponent } from './components/whb-banner/whb-banner.component';
import { ValidationsDirective } from 'src/app/shared/directives/validations.directive';
import { ExactTargetEmailComponent } from './components/exact-target-email/exact-target-email.component';
import { MatIconModule } from '@angular/material/icon';
import { ContentStorageComponent } from './components/content-storage/content-storage.component';
import { RedirectService } from './services/redirect.service';
import { FindCustomerComponent } from './components/find-customer/find-customer.component';
import { MemberCardComponent } from './components/member-card/member-card.component';
import { IndividualMarketConsumerReferralEmailComponent } from './components/individual-market-consumer-referral-email/individual-market-consumer-referral-email.component';
import { environment } from 'src/environments/environment';
import { SbcNavigationComponent } from './components/sbc-navigation/sbc-navigation.component';
import { NoEnrollCatastrophicModalComponent } from './components/enroll-button/on-exchange-modal-popups/no-enroll-catastrophic-modal/no-enroll-catastrophic-modal.component';
import { WarnEnrollNonSilverComponent } from './components/enroll-button/on-exchange-modal-popups/warn-enroll-non-silver/warn-enroll-non-silver.component';
import { FormNumberComponent } from './components/form-number/form-number.component';
import { PtlSessionLoaderComponent } from './components/ptl-session-loader/ptl-session-loader.component';
import { SmallGroupSbcComponent } from './components/small-group-sbc/small-group-sbc.component';
import { AemDocumentSearchComponent } from './components/aem-document-search/aem-document-search.component';
import { PtlXhrModule } from '@ptl/xhr';
import { PtlAccountManagementModule } from '@ptl/account-management';
import { PtlGlobalComponentsModule } from '@ptl/global-components';
import { SbcsAndAgreementsComponent } from './components/sbcs-and-agreements/sbcs-and-agreements.component';
import { SubordinateFilterPipe } from './components/aem-document-search/pipes/subordinate-filter.pipe';
import { ProducerTelesalesContentFilterComponent } from './components/producer-telesales-content-filter/producer-telesales-content-filter.component';
import { GlossarySearchComponent } from '../glossary-search/glossary-search.component';
import { PopulatedSpecialEnrollmentFormPrintComponent } from './components/print-button/variations/populated-special-enrollment-form-print/populated-special-enrollment-form-print.component';
import { PlanDetailsPdfPrintComponent } from './components/print-button/variations/plan-details-pdf-print/plan-details-pdf-print.component';
import { SubmittedApplicationPdfPrint } from './components/print-button/variations/submitted-application-pdf-print/submitted-application-pdf-print.component';
import { PtlFormsModule } from '@ptl/forms';
import { InternalDataRedirectComponent } from './components/internal-data-redirect/internal-data-redirect.component';
import { ConfirmationPageComponent } from './components/confirmation-page/confirmation-page.component';
import { ConfirmationPageMarketplaceComponent } from './components/confirmation-page-marketplace/confirmation-page-marketplace.component';
import { ConfirmationPageAddendumComponent } from './components/confirmation-page-addendum/confirmation-page-addendum.component';
import { StateModuleReadyService } from '@ptl/state';
import { FlipCardModule } from './components/flip-card/flip-card.module';
import { SpecialEnrollmentEventSelectorCardsComponent } from './components/special-enrollment-event-selector-cards/special-enrollment-event-selector-cards.component';
import { DobCheckModalComponent } from './components/dob-check-modal/dob-check-modal.component';
import { DobCheckModalTriggerComponent } from './components/dob-check-modal-trigger/dob-check-modal-trigger.component';
import { MessageCatalogModalComponent } from './components/message-catalog-modal/message-catalog-modal.component';
import { MessageCatalogModalTriggerComponent } from './components/message-catalog-modal-trigger/message-catalog-modal-trigger.component';
import { PlanYearContentFilterComponent } from './components/plan-year-content-filter/plan-year-content-filter.component';
import { TruncatePipe } from './pipes/truncate.pipe';
import { CustomMomentDateAdapter } from 'src/app/shared/providers/custom-moment-date.adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { DragAndDropFileUploadComponent } from './components/drag-and-drop-file-upload/drag-and-drop-file-upload.component';
import { DocUploadSuccessComponent } from './components/doc-upload-success/doc-upload-success.component';
import { DataDisplayCardComponent } from './components/data-display-card/data-display-card.component';
import { AgActionMenuCellRendererComponent } from './components/ag-grid-cell-renderers/ag-action-menu-cell-renderer/ag-action-menu-cell-renderer.component';
import { AgQuickActionsCellRendererComponent } from './components/ag-grid-cell-renderers/ag-quick-actions-cell-renderer/ag-quick-actions-cell-renderer.component';
import { AgCustomDropdownCellRendererComponent } from './components/ag-grid-cell-renderers/ag-custom-dropdown-cell-renderer/ag-custom-dropdown-cell-renderer.component';
import { AgDetailNotesRendererComponent } from './components/ag-grid-cell-renderers/ag-detail-notes-renderer/ag-detail-notes-renderer.component';

const MY_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@NgModule({
  imports: [
    CommonModule,
    MaterialGlobalModule,
    ReducersRegistryModule,
    PtlXhrModule,
    PtlAccountManagementModule.forRoot(environment),
    ReactiveFormsModule,
    FormsModule,
    NgReduxModule,
    NgReduxFormModule,
    PtlGlobalComponentsModule,
    MatIconModule,
    PtlFormsModule,
    FlipCardModule,
    MatMenuModule
  ],
  declarations: [
    EnrollButtonComponent,
    CurrentCountyAndZipChangeComponent,
    GlossarySearchComponent,
    LeavingGateModalComponent,
    LoginPageComponent,
    PrintButtonComponent,
    RegistrationComponent,
    ForgotPasswordComponent,
    NeedMoreHelpComponent,
    NeedMoreHelpModalComponent,
    PasswordResetComponent,
    AppStateAsContentComponent,
    LogoutComponent,
    EmailPageComponent,
    GlobalHiddenComponent,
    MemberLookupComponent,
    RrdLinkComponent,
    WhbBannerComponent,
    ValidationsDirective,
    ExactTargetEmailComponent,
    IndividualMarketConsumerReferralEmailComponent,
    ContentStorageComponent,
    FindCustomerComponent,
    MemberCardComponent,
    SbcNavigationComponent,
    NoEnrollCatastrophicModalComponent,
    WarnEnrollNonSilverComponent,
    FormNumberComponent,
    PtlSessionLoaderComponent,
    SmallGroupSbcComponent,
    AemDocumentSearchComponent,
    SbcsAndAgreementsComponent,
    SubordinateFilterPipe,
    ProducerTelesalesContentFilterComponent,
    PopulatedSpecialEnrollmentFormPrintComponent,
    PlanDetailsPdfPrintComponent,
    SubmittedApplicationPdfPrint,
    InternalDataRedirectComponent,
    ConfirmationPageComponent,
    ConfirmationPageMarketplaceComponent,
    ConfirmationPageAddendumComponent,
    DocUploadSuccessComponent,
    SpecialEnrollmentEventSelectorCardsComponent,
    DobCheckModalComponent,
    DobCheckModalTriggerComponent,
    MessageCatalogModalComponent,
    MessageCatalogModalTriggerComponent,
    PlanYearContentFilterComponent,
    TruncatePipe,
    DragAndDropFileUploadComponent,
    AgActionMenuCellRendererComponent,
    AgCustomDropdownCellRendererComponent,
    AgQuickActionsCellRendererComponent,
    AgDetailNotesRendererComponent,
    DataDisplayCardComponent
  ],
  exports: [
    EnrollButtonComponent,
    CurrentCountyAndZipChangeComponent,
    RegistrationComponent,
    ForgotPasswordComponent,
    PrintButtonComponent,
    PopulatedSpecialEnrollmentFormPrintComponent,
    SubmittedApplicationPdfPrint,
    PlanDetailsPdfPrintComponent,
    ValidationsDirective,
    LoginPageComponent,
    MemberLookupComponent,
    ConfirmationPageComponent,
    ConfirmationPageAddendumComponent,
    DragAndDropFileUploadComponent,
    DocUploadSuccessComponent,
    FlipCardModule,
    TruncatePipe,
    DataDisplayCardComponent
  ],
  providers: [
    CookieService, 
    RedirectService,
    {
      provide: MatDialogRef,
      useValue: []
    },
    {
      provide: MAT_DIALOG_DATA,
      useValue: {}
    },
    CustomMomentDateAdapter,
    {
      provide: DateAdapter,
      useExisting: CustomMomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})

export class SharedComponentsModule {
  constructor(
    private injector: Injector,
    private stateModuleReadyService: StateModuleReadyService
  ) {
    // console.log("SharedComponentsModule - constructor");
    this.stateModuleReadyService.ready$.subscribe((isReady: boolean) => {
      if (isReady) {
        this.createElementIfDoesNotExist('sxe-login', LoginPageComponent);
        this.createElementIfDoesNotExist('sxe-need-more-help', NeedMoreHelpComponent);
        this.createElementIfDoesNotExist('sxe-pwd-reset', PasswordResetComponent);
        this.createElementIfDoesNotExist('sxe-print-button', PrintButtonComponent);
        this.createElementIfDoesNotExist('sxe-forgot-password', ForgotPasswordComponent);
        this.createElementIfDoesNotExist('sxe-registration', RegistrationComponent);
        this.createElementIfDoesNotExist('sxe-app-state-as-content', AppStateAsContentComponent);
        this.createElementIfDoesNotExist('sxe-logout', LogoutComponent);
        this.createElementIfDoesNotExist('sxe-email', EmailPageComponent);
        this.createElementIfDoesNotExist('sxe-global-hidden', GlobalHiddenComponent);
        this.createElementIfDoesNotExist('sxe-member-lookup', MemberLookupComponent);
        this.createElementIfDoesNotExist('sxe-rrd-link', RrdLinkComponent);
        this.createElementIfDoesNotExist('sxe-whb-banner', WhbBannerComponent);
        this.createElementIfDoesNotExist('sxe-exact-target-email', ExactTargetEmailComponent);
        this.createElementIfDoesNotExist('sxe-individual-market-consumer-referral-email', IndividualMarketConsumerReferralEmailComponent);
        this.createElementIfDoesNotExist('sxe-change-current-zip-county', CurrentCountyAndZipChangeComponent);
        this.createElementIfDoesNotExist('sxe-content-storage', ContentStorageComponent);
        this.createElementIfDoesNotExist('sxe-find-customer', FindCustomerComponent);
        this.createElementIfDoesNotExist('sxe-sbc-navigation', SbcNavigationComponent);
        this.createElementIfDoesNotExist('sxe-form-number', FormNumberComponent);
        this.createElementIfDoesNotExist('sxe-session-loader', PtlSessionLoaderComponent);
        this.createElementIfDoesNotExist('sxe-small-group-sbc', SmallGroupSbcComponent);
        this.createElementIfDoesNotExist('sxe-aem-document-search', AemDocumentSearchComponent);
        this.createElementIfDoesNotExist('sxe-sbcs-and-agreements', SbcsAndAgreementsComponent);
        this.createElementIfDoesNotExist('sxe-producer-telesales-content-filter', ProducerTelesalesContentFilterComponent);
        this.createElementIfDoesNotExist('sxe-plan-year-content-filter', PlanYearContentFilterComponent);
        this.createElementIfDoesNotExist('sxe-glossary-search', GlossarySearchComponent);
        this.createElementIfDoesNotExist('sxe-internal-data-redirect', InternalDataRedirectComponent);
        this.createElementIfDoesNotExist('sxe-confirmation-page', ConfirmationPageComponent);
        this.createElementIfDoesNotExist('sxe-confirmation-page-marketplace', ConfirmationPageMarketplaceComponent);
        this.createElementIfDoesNotExist('sxe-confirmation-page-addendum', ConfirmationPageAddendumComponent);
        this.createElementIfDoesNotExist('sxe-doc-upload-success', DocUploadSuccessComponent);
        this.createElementIfDoesNotExist('sxe-special-enrollment-event-selector-cards-component', SpecialEnrollmentEventSelectorCardsComponent);
        this.createElementIfDoesNotExist('sxe-dob-check-modal-trigger', DobCheckModalTriggerComponent);
        this.createElementIfDoesNotExist('sxe-message-catalog-modal-trigger', MessageCatalogModalTriggerComponent);
      }
    });
    ModuleRegistry.registerModules([ 
      InfiniteRowModelModule,
      TextFilterModule,
      ServerSideRowModelModule,
      MasterDetailModule,
      RichSelectModule,
      ColumnsToolPanelModule,
      ValidationModule 
    ]);
    LicenseManager.setLicenseKey("Using_this_{AG_Grid}_Enterprise_key_{AG-129056}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Highmark_Inc}_is_granted_a_{Multiple_Applications}_Developer_License_for_{29}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{6}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{31_May_2027}____[v2]_[01]_MTgxMTcxODAwMDAwMA==b63adc83a4cbf4ce9da8c786fd1d1afa");
  }

  /** Adding these checks in, as during debugging, sometimes the error of previous definition would be thrown */
  createElementIfDoesNotExist(elementName: string, elementComponent: any) {
    if (!customElements.get(elementName)) {
      const customElement = createCustomElement(elementComponent, {
        injector: this.injector
      });
      customElements.define(elementName, customElement);
    }
  }
  ngDoBootstrap() { }
}
